import React, { useState, useEffect, useMemo } from 'react';
import { Package2, Ruler, Palette, Box, Truck, Scale, Maximize2, Minimize2, HelpCircle, Plus, Minus, Layers, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { BoxSpecifications, Feature, CorrugatedType, DimensionUnit, SkuItem, InsertSpecification } from '../types';
import { InsertSection, getDefaultInsertSpec } from './quote/sections/InsertSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/Tabs';
import { Button } from './ui/Button';
import { NumberInput } from './ui/NumberInput';
import { SelectInput } from './ui/SelectInput';
import { CheckboxInput } from './ui/CheckboxInput';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';
import { Switch } from './ui/switch';
import { cn } from '../lib/utils';
import { useMaterials } from '../hooks/useMaterials';
import { useBoxTypes } from '../hooks/useBoxTypes';
import { calculateDimensions } from '../utils/api';
import { convertToInches, convertFromInches, roundToDecimal } from '../utils/unitConversion';
import { useTheme } from '../contexts/ThemeContext';
import { SkuQuantityManager, OrderMode } from './SkuQuantityManager';

interface StepperFormProps {
  specs: BoxSpecifications;
  inputMode: 'dimensions' | 'openSize';
  openDimensions: { openLength: number; openWidth: number };
  loading?: boolean;
  onInputModeToggle: () => void;
  onSpecsChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onLaminationBothSidesChange: (value: boolean) => void;
  onWhiteUnderprintChange: (value: boolean) => void;
  onPrintingChange: (type: 'cmyk' | 'pantone' | 'metallic', value: number, isInside: boolean) => void;
  onFeatureAreaChange: (feature: 'embossing' | 'debossing' | 'foiling' | 'spot-uv' | 'pvc-window', length: number, width: number) => void;
  onCorrugatedTypeChange: (corrugatedType: CorrugatedType) => void;
  onOpenDimensionsCalculated?: (openDimensions: { openLength: number; openWidth: number }) => void;
  onSubmit: (e: React.FormEvent) => void;
  // Order mode (single, compare quantities, multi-design)
  orderMode?: OrderMode;
  onOrderModeChange?: (mode: OrderMode) => void;
  // Multi-SKU/Multi-Design support
  skus?: SkuItem[];
  onSkusChange?: (skus: SkuItem[]) => void;
  // Multi-quantity comparison support
  quantityTiers?: number[];
  onQuantityTiersChange?: (tiers: number[]) => void;
  selectedTierIndex?: number;
  onSelectedTierChange?: (index: number) => void;
  tierSavings?: number[];
  // Button state control
  hasFormChanged?: boolean;
  hasSuccessfulCalculation?: boolean;
  // Shipping options support
  allShippingOptions?: Record<string, Record<string, { cost?: number } & Record<string, unknown>>> | null;
  selectedShipping?: { type: string; option: string } | null;
  onShippingSelect?: (type: string, option: string) => void;
  weight?: number; // Total weight in kg
  // Company profit margin controls
  companyProfit?: number;
  onCompanyProfitChange?: (value: number) => void;
  // Insert support
  insertSpec?: InsertSpecification;
  onInsertChange?: (spec: InsertSpecification) => void;
}

// Removed hardcoded BOX_TYPE_NAMES - now using dynamic box types from backend

// Simple local calculation for UI preview only - matches backend formulas exactly
// The actual calculation is done by the backend API
const calculateOpenDimensionsPreview = (boxType: string, length: number, width: number, height: number) => {
  const round = (num: number) => Math.round(num * 100) / 100;
  let openLength = 0;
  let openWidth = 0;
  
  switch(boxType) {
    case 'tuck-top-snap-lock':
      openLength = round(2 * width + height + 0.75) + 1.0;
      openWidth = round(2 * length + 2 * width + 0.375) + 1.0;
      break;
    
    case 'autolock-bottom':
      openLength = round(1.625 * width + height + 0.75) + 1.0;
      openWidth = round(2 * length + 2 * width + 0.375) + 1.0;
      break;
    
    case 'reverse-tuck':
      openLength = round(2 * width + height + 1) + 1.0;
      openWidth = round(2 * length + 2 * width + 0.75) + 1.0;
      break;
    
    case 'mailer-box':
      openLength = round(2 * width + 3 * height + 0.188) + 1.0;
      openWidth = round(length + 4 * height + 0.5) + 1.0;
      break;
    
    case 'two-piece':
      openLength = round(width + 2 * height + 2) + 1.0;
      openWidth = round(length + 2 * height + 2) + 1.0;
      break;
    
    case 'pillow-box':
      openLength = round(length + height / 1.75 + height) + 1.0;
      openWidth = round(2 * length + 2 * width + 0.5) + 1.0;
      break;
    
    case 'tray-only':
      openLength = round(length + 2 * height) + 1.0;
      openWidth = round(width + 4 * height + 2) + 1.0;
      break;
    
    case 'gable-box':
      openLength = round(height + 2 * width) + 1.0;
      openWidth = round(2 * length + 2 * width + 1.5) + 1.0;
      break;
    
    case 'cake-box':
      openLength = round(length + 2 * height - 2) + 1.0;
      openWidth = round(2 * width + 2 * height + 1.5) + 1.0;
      break;
    
    default:
      // Fallback to tuck-top-snap-lock formula for unknown types
      openLength = round(2 * width + height + 0.75) + 1.0;
      openWidth = round(2 * length + 2 * width + 0.375) + 1.0;
  }
  
  return { openLength, openWidth };
};

export const StepperForm: React.FC<StepperFormProps> = ({
  specs,
  inputMode,
  openDimensions,
  loading = false,
  onInputModeToggle,
  onSpecsChange,
  onLaminationBothSidesChange,
  onWhiteUnderprintChange,
  onPrintingChange,
  onFeatureAreaChange,
  onCorrugatedTypeChange,
  onOpenDimensionsCalculated,
  onSubmit,
  orderMode = 'single',
  onOrderModeChange,
  skus = [],
  onSkusChange,
  quantityTiers = [],
  onQuantityTiersChange,
  selectedTierIndex = 0,
  onSelectedTierChange,
  tierSavings = [],
  hasFormChanged = false,
  hasSuccessfulCalculation = false,
  allShippingOptions = null,
  selectedShipping = null,
  onShippingSelect,
  weight = 0,
  companyProfit = 0,
  onCompanyProfitChange,
  insertSpec,
  onInsertChange,
}) => {
  const { actualTheme } = useTheme();
  const isDarkMode = actualTheme === 'dark';
  const [activeTab, setActiveTab] = useState('dimensions');
  const [showAdditionalFeatures, setShowAdditionalFeatures] = useState(false);
  const [showInsertSection, setShowInsertSection] = useState(false);
  
  // Local insert state - synced with specs.insert
  const [localInsertSpec, setLocalInsertSpec] = useState<InsertSpecification>(
    specs.insert ?? getDefaultInsertSpec()
  );
  const currentInsertSpec = insertSpec ?? localInsertSpec;
  
  // Handle insert changes - update both local state and parent specs
  const handleInsertChange = (newSpec: InsertSpecification) => {
    setLocalInsertSpec(newSpec);
    if (onInsertChange) {
      onInsertChange(newSpec);
    }
    // Update specs.insert so it gets sent to API
    onSpecsChange({ 
      target: { name: 'insert', value: newSpec.enabled ? newSpec : undefined } 
    } as React.ChangeEvent<HTMLInputElement>);
  };
  const [showValidationErrors, setShowValidationErrors] = useState(false); // Only show after submit attempt
  const [calculatedWeight, setCalculatedWeight] = useState<number>(0); // Weight in kg for filtering shipping options
  const isCorrugatedSingle = specs.material === 'corrugated' && (specs.corrugatedType === 'single-kraft' || specs.corrugatedType === 'single-cardboard');
  // 420/460 GSM now supports inside printing (engineered from 2× base sheets)
  const insidePrintingDisabled = isCorrugatedSingle;
  
  // Get dimension unit from specs, default to 'in'
  const dimensionUnit = specs.dimensionUnit || 'in';
  
  // Handle unit change
  const handleUnitChange = (newUnit: 'in' | 'mm' | 'cm') => {
    const currentUnit = dimensionUnit;
    
    // Convert current values from inches to display values in current unit
    const currentLength = convertFromInches(specs.length, currentUnit);
    const currentWidth = convertFromInches(specs.width, currentUnit);
    const currentHeight = convertFromInches(specs.height, currentUnit);
    
    // Convert those display values to the new unit (via inches)
    const newLength = convertToInches(currentLength, currentUnit);
    const newWidth = convertToInches(currentWidth, currentUnit);
    const newHeight = convertToInches(currentHeight, currentUnit);
    
    // Update specs with the unit change (dimensions stay in inches internally)
    onSpecsChange({ target: { name: 'dimensionUnit', value: newUnit } } as React.ChangeEvent<HTMLInputElement>);
  };
  
  // Get display values (convert from inches to selected unit)
  const displayLength = roundToDecimal(convertFromInches(specs.length, dimensionUnit));
  const displayWidth = roundToDecimal(convertFromInches(specs.width, dimensionUnit));
  const displayHeight = roundToDecimal(convertFromInches(specs.height, dimensionUnit));
  
  // Validation: Check if required dimensions are provided
  const hasValidDimensions = inputMode === 'openSize' 
    ? (openDimensions.openLength > 0 && openDimensions.openWidth > 0)
    : (specs.length > 0 && specs.width > 0 && specs.height > 0);
  
  const hasValidQuantity = specs.quantity > 0;
  const isFormValid = hasValidDimensions && hasValidQuantity;
  
  // Prevent accidental form submission with Enter on non-final steps
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    const tag = target?.tagName?.toLowerCase();
    const isTextArea = tag === 'textarea';
    const isButton = tag === 'button';
    // Only allow Enter to submit on the last step, otherwise prevent default
    const tabsOrder = ['dimensions', 'material', 'printing', 'shipping'];
    const isLastStep = activeTab === tabsOrder[tabsOrder.length - 1];
    if (!isTextArea && !isButton && !isLastStep) {
      e.preventDefault();
    }
  };
  
  // State for real-time dimension calculation from backend
  const [realTimeDimensions, setRealTimeDimensions] = useState<{
    openLength: number;
    openWidth: number;
    loading: boolean;
    error: string | null;
  }>({
    openLength: 0,
    openWidth: 0,
    loading: false,
    error: null
  });
  
  // Hook to fetch dynamic GSM values from backend
  const { materials, loading: materialsLoading } = useMaterials();
  
  // Hook to fetch dynamic box types from backend
  const { boxTypes, loading: boxTypesLoading, error: boxTypesError } = useBoxTypes();

  // Get GSM options for the current material
  const getGsmOptions = (material: string) => {
    const materialData = materials[material];
    if (!materialData?.gsmValues) {
      // Fallback to hardcoded values if backend data is not available
      switch (material) {
        case 'cardboard':
          return [210, 250, 270, 300, 350, 420, 460].map(gsm => ({ value: gsm.toString(), label: `${gsm} GSM` }));
        case 'rigid':
          return [350, 400, 450, 500, 550].map(gsm => ({ value: gsm.toString(), label: `${gsm} GSM` }));
        case 'kraft':
          return [210, 230, 250, 270, 280, 290, 300, 320, 330, 350, 380, 400].map(gsm => ({ value: gsm.toString(), label: `${gsm} GSM` }));
        default:
          return [];
      }
    }
    
    // Use dynamic values from backend
    return Object.keys(materialData.gsmValues)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(gsm => ({ value: gsm, label: `${gsm} GSM` }));
  };
  
  // Helper function to render area inputs for premium features
  const renderAreaInputs = (feature: 'embossing' | 'debossing' | 'foiling' | 'spot-uv' | 'pvc-window', displayName: string) => {
    if (!specs.features.includes(feature)) return null;
    
    const currentArea = specs.featureAreas[feature] || { length: 0, width: 0 };
    const areaInSqIn = currentArea.length * currentArea.width;
    const hasValidArea = currentArea.length > 0 && currentArea.width > 0;
    
    return (
      <div className="mt-3 p-3 bg-muted/50 rounded-md border border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
            <span className="text-xs font-medium text-muted-foreground">{displayName} Area</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-card-foreground">{areaInSqIn.toFixed(1)}</span>
            <span className="text-xs text-muted-foreground">sq in</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <input
              type="number"
              value={currentArea.length || ''}
              onChange={(e) => onFeatureAreaChange(feature, Number(e.target.value) || 0, currentArea.width)}
              min="0.1"
              step="0.1"
              className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground"
              placeholder="0"
            />
            <span className="text-xs text-muted-foreground ml-1">Length</span>
          </div>
          
          <span className="text-muted-foreground text-xs px-1">×</span>
          
          <div className="flex-1">
            <input
              type="number"
              value={currentArea.width || ''}
              onChange={(e) => onFeatureAreaChange(feature, currentArea.length, Number(e.target.value) || 0)}
              min="0.1"
              step="0.1"
              className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground"
              placeholder="0"
            />
            <span className="text-xs text-muted-foreground ml-1">Width</span>
          </div>
          
          <span className="text-xs text-muted-foreground">in</span>
        </div>
      </div>
    );
  };
  
  // Real-time dimension calculation from backend API
  useEffect(() => {
    const fetchDimensions = async () => {
      if (!specs.boxType || !specs.length || !specs.width || !specs.height) {
        // Reset to 0 when inputs are invalid
        setRealTimeDimensions({ openLength: 0, openWidth: 0, loading: false, error: null });
        return;
      }

      setRealTimeDimensions(prev => ({ ...prev, loading: true, error: null }));

      try {
        const response = await calculateDimensions(specs.boxType, specs.length, specs.width, specs.height);
        const calculatedDimensions = {
          openLength: response.data.openDimensions.openLength,
          openWidth: response.data.openDimensions.openWidth,
          loading: false,
          error: null
        };
        setRealTimeDimensions(calculatedDimensions);
        
        // Call the callback to pass dimensions to parent
        if (onOpenDimensionsCalculated) {
          onOpenDimensionsCalculated({
            openLength: calculatedDimensions.openLength,
            openWidth: calculatedDimensions.openWidth
          });
        }
      } catch (error) {
        console.error('Failed to calculate dimensions:', error);
        // Fallback to static calculation on error
        const staticDimensions = calculateOpenDimensionsPreview(specs.boxType, specs.length, specs.width, specs.height);
        const fallbackDimensions = {
          openLength: staticDimensions.openLength,
          openWidth: staticDimensions.openWidth,
          loading: false,
          error: 'Using static calculation due to API error'
        };
        setRealTimeDimensions(fallbackDimensions);
        
        // Call the callback even for fallback dimensions
        if (onOpenDimensionsCalculated) {
          onOpenDimensionsCalculated({
            openLength: fallbackDimensions.openLength,
            openWidth: fallbackDimensions.openWidth
          });
        }
      }
    };

    fetchDimensions();
  }, [specs.boxType, specs.length, specs.width, specs.height]);

  React.useEffect(() => {
    import('../types').then(({ MATERIAL_CONFIGS }) => {
      const defaultGsm = MATERIAL_CONFIGS[specs.material].gsm.default;
      
      if (specs.gsm !== defaultGsm) {
        onSpecsChange({
          target: { 
            name: 'gsm', 
            value: defaultGsm.toString(), 
            type: 'select-one'
          }
        } as unknown as React.ChangeEvent<HTMLSelectElement>);
      }
    });
  }, [specs.material]);

  const tabs = [
    {
      id: 'dimensions',
      title: 'Dimensions',
      icon: Ruler,
    },
    {
      id: 'material',
      title: 'Material',
      icon: Box,
    },
    {
      id: 'printing',
      title: 'Printing',
      icon: Palette,
    },
    {
      id: 'shipping',
      title: 'Shipping',
      icon: Truck,
    },
  ];

  // Calculate product weight for shipping filtering
  const calculateProductWeight = () => {
    // Use the same calculation as backend
    const gsmValue = typeof specs.gsm === 'string' ? parseInt(specs.gsm, 10) : specs.gsm;
    
    // Get open dimensions
    const openLen = inputMode === 'openSize' ? openDimensions.openLength : realTimeDimensions.openLength;
    const openWid = inputMode === 'openSize' ? openDimensions.openWidth : realTimeDimensions.openWidth;
    
    // Calculate area in square inches
    const areaInSquareInches = openLen * openWid;
    
    // Convert to square meters
    const areaInSquareMeters = areaInSquareInches * 0.00064516;
    
    // Calculate weight: (area_m² × GSM) / 1000
    const weightPerBox = (areaInSquareMeters * gsmValue) / 1000;
    
    // Total weight for quantity
    const totalWeight = weightPerBox * specs.quantity;
    
    return totalWeight;
  };

  // Single highest-cost option per category for shipping section (express / air / sea)
  const shippingOptionsHighestOnly = useMemo(() => {
    const exchangeRate = 275;
    const currentWeight = weight ?? calculatedWeight ?? 0;
    const country = specs.shippingCountry || 'USA';
    const out: Array<{ type: 'express' | 'airFreight' | 'seaFreight'; key: string; displayName: string; cost: number; costUSD: number; deliveryDays: string }> = [];

    if (!allShippingOptions) return out;

    // Express: same filters as below (country + FEDEX weight), pick highest cost
    if (allShippingOptions.express) {
      let best: { key: string; carrier: string; cost: number } | null = null;
      Object.entries(allShippingOptions.express).forEach(([key, data]) => {
        const [carrier, destination] = key.split('_');
        if (destination && destination !== country) return;
        if (carrier === 'FEDEX' && destination === 'USA' && currentWeight < 22) return;
        const cost = typeof data.cost === 'number' ? data.cost : 0;
        if (!best || cost > best.cost) best = { key, carrier, cost };
      });
      if (best) out.push({ type: 'express', key: best.key, displayName: `${best.carrier} Express`, cost: best.cost, costUSD: best.cost / exchangeRate, deliveryDays: '5-7 days' });
    }

    // Air Freight: country only (exclude DHL), pick highest cost
    if (allShippingOptions.airFreight) {
      let best: { key: string; displayName: string; cost: number } | null = null;
      Object.entries(allShippingOptions.airFreight).forEach(([key, data]) => {
        if (key === 'DHL') return;
        if (key !== country) return;
        const cost = typeof data.cost === 'number' ? data.cost : 0;
        const displayName = 'Air Freight';
        if (!best || cost > best.cost) best = { key, displayName, cost };
      });
      if (best) out.push({ type: 'airFreight', key: best.key, displayName: best.displayName, cost: best.cost, costUSD: best.cost / exchangeRate, deliveryDays: '10-14 days' });
    }

    // Sea Freight: country only, pick highest cost (usually one)
    if (allShippingOptions.seaFreight) {
      let best: { key: string; cost: number } | null = null;
      Object.entries(allShippingOptions.seaFreight).forEach(([key, data]) => {
        if (key !== country) return;
        const cost = typeof data.cost === 'number' ? data.cost : 0;
        if (!best || cost > best.cost) best = { key, cost };
      });
      if (best) out.push({ type: 'seaFreight', key: best.key, displayName: 'Sea Freight', cost: best.cost, costUSD: best.cost / exchangeRate, deliveryDays: '30-60 days' });
    }

    return out;
  }, [allShippingOptions, specs.shippingCountry, weight, calculatedWeight]);

  // Handle next button click with validation
  const handleNextClick = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    
    // If on dimensions tab, validate before allowing next
    if (activeTab === 'dimensions' && !hasValidDimensions) {
      setShowValidationErrors(true);
      return;
    }
    
    // If moving from material step to shipping, calculate weight
    if (activeTab === 'material' && currentIndex < tabs.length - 1) {
      const weight = calculateProductWeight();
      setCalculatedWeight(weight);
    }
    
    // Move to next tab
    if (currentIndex < tabs.length - 1) {
      const nextId = tabs[currentIndex + 1].id;
      setTimeout(() => setActiveTab(nextId), 0);
    }
  };

  return (
    <form onSubmit={onSubmit} onKeyDown={handleFormKeyDown} className="w-full min-h-[500px] flex flex-col">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col flex-1">
        <div className="p-6 pb-0">
          <TabsList className="grid w-full grid-cols-4 h-10 bg-muted">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2 text-sm h-8 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.title}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="p-6 space-y-6 flex-1">
            <TabsContent value="dimensions" className="space-y-4 mt-0">
              {/* Header with Mode Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-primary rounded-full"></div>
                  <span className="text-sm font-medium text-foreground">
                    {inputMode === 'dimensions' ? 'Box Dimensions' : 'Open Size'}
                  </span>
                </div>
                <Button 
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onInputModeToggle}
                  disabled={loading}
                  className="h-6 px-2 text-xs"
                >
                  {inputMode === 'dimensions' ? (
                    <>
                      <Maximize2 className="h-3 w-3 mr-1" />
                      Open Size
                    </>
                  ) : (
                    <>
                      <Minimize2 className="h-3 w-3 mr-1" />
                      Dimensions
                    </>
                  )}
                </Button>
              </div>
              
              {inputMode === 'dimensions' ? (
                <div className="space-y-4">
                  {/* Box Type and Unit Selector Row */}
                  <div className="grid grid-cols-[1fr,auto] gap-4 items-end">
                    <SelectInput
                      label="Box Type"
                      name="boxType"
                      value={specs.boxType}
                      onChange={onSpecsChange}
                      options={boxTypes.map(boxType => ({
                        value: boxType.name,
                        label: boxType.displayName,
                        disabled: boxTypesLoading
                      }))}
                      tooltip={boxTypesError || "Select the type of box you want to create"}
                    />
                    <SelectInput
                      label="Unit"
                      name="dimensionUnit"
                      value={dimensionUnit}
                      onChange={(e) => handleUnitChange(e.target.value as 'in' | 'mm' | 'cm')}
                      options={[
                        { value: 'in', label: 'in' },
                        { value: 'mm', label: 'mm' },
                        { value: 'cm', label: 'cm' }
                      ]}
                      className="w-[120px]"
                    />
                  </div>

                  {/* Dimensions Grid */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Length (Left to Right)</label>
                      <div className="relative">
                        <input
                          type="number"
                          name="length"
                          value={displayLength || ''}
                          onChange={(e) => {
                            const displayValue = parseFloat(e.target.value) || 0;
                            const inchesValue = convertToInches(displayValue, dimensionUnit);
                            onSpecsChange({ ...e, target: { ...e.target, name: 'length', value: roundToDecimal(inchesValue, 4), type: 'number' } });
                          }}
                          min={0.1}
                          step={dimensionUnit === 'in' ? 0.1 : dimensionUnit === 'cm' ? 0.5 : 1}
                          className={cn(
                            "flex h-9 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 pr-8",
                            showValidationErrors && specs.length <= 0 ? "border-destructive focus-visible:ring-destructive" : "border-input"
                          )}
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{dimensionUnit}</span>
                      </div>
                      {showValidationErrors && specs.length <= 0 && <p className="text-xs text-destructive">Length must be greater than 0</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Width (Front to Back)</label>
                      <div className="relative">
                        <input
                          type="number"
                          name="width"
                          value={displayWidth || ''}
                          onChange={(e) => {
                            const displayValue = parseFloat(e.target.value) || 0;
                            const inchesValue = convertToInches(displayValue, dimensionUnit);
                            onSpecsChange({ ...e, target: { ...e.target, name: 'width', value: roundToDecimal(inchesValue, 4), type: 'number' } });
                          }}
                          min={0.1}
                          step={dimensionUnit === 'in' ? 0.1 : dimensionUnit === 'cm' ? 0.5 : 1}
                          className={cn(
                            "flex h-9 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 pr-8",
                            showValidationErrors && specs.width <= 0 ? "border-destructive focus-visible:ring-destructive" : "border-input"
                          )}
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{dimensionUnit}</span>
                      </div>
                      {showValidationErrors && specs.width <= 0 && <p className="text-xs text-destructive">Width must be greater than 0</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Height (Top to Bottom)</label>
                      <div className="relative">
                        <input
                          type="number"
                          name="height"
                          value={displayHeight || ''}
                          onChange={(e) => {
                            const displayValue = parseFloat(e.target.value) || 0;
                            const inchesValue = convertToInches(displayValue, dimensionUnit);
                            onSpecsChange({ ...e, target: { ...e.target, name: 'height', value: roundToDecimal(inchesValue, 4), type: 'number' } });
                          }}
                          min={0.1}
                          step={dimensionUnit === 'in' ? 0.1 : dimensionUnit === 'cm' ? 0.5 : 1}
                          className={cn(
                            "flex h-9 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 pr-8",
                            showValidationErrors && specs.height <= 0 ? "border-destructive focus-visible:ring-destructive" : "border-input"
                          )}
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{dimensionUnit}</span>
                      </div>
                      {showValidationErrors && specs.height <= 0 && <p className="text-xs text-destructive">Height must be greater than 0</p>}
                    </div>
                  </div>

                  {/* Open Size */}
                  <div className="p-4 bg-muted rounded-md border border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-card-foreground">Open Size:</span>
                      <div className="flex items-center gap-2">
                        {realTimeDimensions.loading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Calculating...</span>
                          </div>
                        ) : (
                          <>
                            <span className="text-sm font-semibold text-foreground font-mono">
                              {convertFromInches(realTimeDimensions.openLength, dimensionUnit).toFixed(2)} {dimensionUnit} × {convertFromInches(realTimeDimensions.openWidth, dimensionUnit).toFixed(2)} {dimensionUnit}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Open Dimensions Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Open Length</label>
                      <div className="relative">
                        <input
                          type="number"
                          name="openLength"
                          value={openDimensions.openLength || ''}
                          onChange={onSpecsChange}
                          min={2}
                          step={0.1}
                          className={cn(
                            "w-full px-2.5 py-2 text-sm border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground pr-8",
                            showValidationErrors && openDimensions.openLength <= 0 ? "border-destructive focus:ring-destructive" : "border-border"
                          )}
                          placeholder="Length"
                        />
                        <span className="absolute right-2.5 top-2 text-xs text-muted-foreground">in</span>
                      </div>
                      {showValidationErrors && openDimensions.openLength <= 0 && <p className="text-xs text-destructive">Open length must be greater than 0</p>}
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Open Width</label>
                      <div className="relative">
                        <input
                          type="number"
                          name="openWidth"
                          value={openDimensions.openWidth || ''}
                          onChange={onSpecsChange}
                          min={2}
                          step={0.1}
                          className={cn(
                            "w-full px-2.5 py-2 text-sm border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground pr-8",
                            showValidationErrors && openDimensions.openWidth <= 0 ? "border-destructive focus:ring-destructive" : "border-border"
                          )}
                          placeholder="Width"
                        />
                        <span className="absolute right-2.5 top-2 text-xs text-muted-foreground">in</span>
                      </div>
                      {showValidationErrors && openDimensions.openWidth <= 0 && <p className="text-xs text-destructive">Open width must be greater than 0</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping Destination */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Shipping Destination</label>
                <select
                  name="shippingCountry"
                  value={specs.shippingCountry || 'USA'}
                  onChange={onSpecsChange}
                  className="w-full px-3 pr-8 py-2.5 text-sm border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground transition-all"
                >
                  <option value="USA">🇺🇸 United States</option>
                  <option value="UK">🇬🇧 United Kingdom</option>
                  <option value="DUBAI">🇦🇪 Dubai (UAE)</option>
                </select>
              </div>
            </TabsContent>

            <TabsContent value="material" className="space-y-6 mt-0">

              {/* Material & Specifications */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                  <span className="text-sm font-medium text-card-foreground">Material & Specifications</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectInput
                    label="Material"
                    name="material"
                    value={specs.material}
                    onChange={onSpecsChange}
                    options={[
                      { value: 'cardboard', label: 'Cardboard' },
                      { value: 'rigid', label: 'Rigid (Coming Soon)', disabled: true },
                      { value: 'corrugated', label: 'Corrugated' },
                      { value: 'kraft', label: 'Kraft' },
                    ]}
                    tooltip="Choose your material type"
                  />
                  
                  {specs.material === 'corrugated' ? (
                    <SelectInput
                      label="Construction"
                      name="corrugatedType"
                      value={specs.corrugatedType || ''}
                      onChange={(e) => onCorrugatedTypeChange(e.target.value as CorrugatedType)}
                      options={[
                        { value: 'single-kraft', label: 'Single Kraft' },
                        { value: 'single-cardboard', label: 'Single Cardboard' },
                        { value: 'double-kraft', label: 'Double Kraft' },
                        { value: 'double-cardboard', label: 'Double Cardboard' },
                      ]}
                      tooltip="Single: outside printing only, Double: both sides printing"
                    />
                  ) : (
                    <SelectInput
                      label="Thickness (GSM)"
                      name="gsm"
                      value={specs.gsm.toString()}
                      onChange={onSpecsChange}
                      options={getGsmOptions(specs.material)}
                      tooltip="Material thickness in grams per square meter"
                    />
                  )}
                </div>
              </div>
              
              {/* Coating Options */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                  <span className="text-sm font-medium text-card-foreground">Coating Options</span>
                </div>

                <div className="space-y-3">
                  {/* Coating Type - Custom Interactive Radio Buttons */}
                  <div className="flex items-center gap-6">
                    <label
                      className="flex items-center gap-2 text-sm cursor-pointer"
                      onClick={() => {
                        const newFeatures = specs.features.filter(f => f !== 'gloss-coating' && f !== 'matte-coating' && f !== 'soft-touch-coating');
                        onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                        !specs.features.includes('gloss-coating') && !specs.features.includes('matte-coating') && !specs.features.includes('soft-touch-coating')
                          ? 'border-accent bg-accent'
                          : isDarkMode ? 'border-gray-600' : 'border-slate-300'
                      }`}>
                        {!specs.features.includes('gloss-coating') && !specs.features.includes('matte-coating') && !specs.features.includes('soft-touch-coating') && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      None
                    </label>

                    <label
                      className="flex items-center gap-2 text-sm cursor-pointer"
                      onClick={() => {
                        const newFeatures = [...specs.features.filter(f => f !== 'matte-coating' && f !== 'soft-touch-coating'), 'gloss-coating'];
                        onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                        specs.features.includes('gloss-coating')
                          ? 'border-accent bg-accent'
                          : isDarkMode ? 'border-gray-600' : 'border-slate-300'
                      }`}>
                        {specs.features.includes('gloss-coating') && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      Gloss
                    </label>

                    <label
                      className="flex items-center gap-2 text-sm cursor-pointer"
                      onClick={() => {
                        const newFeatures = [...specs.features.filter(f => f !== 'gloss-coating' && f !== 'soft-touch-coating'), 'matte-coating'];
                        onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                        specs.features.includes('matte-coating')
                          ? 'border-accent bg-accent'
                          : isDarkMode ? 'border-gray-600' : 'border-slate-300'
                      }`}>
                        {specs.features.includes('matte-coating') && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      Matte
                    </label>

                    <label
                      className="flex items-center gap-2 text-sm cursor-pointer"
                      onClick={() => {
                        const newFeatures = [...specs.features.filter(f => f !== 'gloss-coating' && f !== 'matte-coating'), 'soft-touch-coating'];
                        onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                        specs.features.includes('soft-touch-coating')
                          ? 'border-accent bg-accent'
                          : isDarkMode ? 'border-gray-600' : 'border-slate-300'
                      }`}>
                        {specs.features.includes('soft-touch-coating') && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      Soft Touch
                    </label>
                  </div>
                  
                  {/* Coverage */}
                  {(specs.features.includes('gloss-coating') || specs.features.includes('matte-coating') || specs.features.includes('soft-touch-coating')) && (
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-600">Coverage</label>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">Outside Only</span>
                        <Switch
                          checked={specs.laminationBothSides}
                          onCheckedChange={onLaminationBothSidesChange}
                        />
                        <span className="text-xs text-muted-foreground">Both Sides</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Features */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                    <span className="text-sm font-medium text-card-foreground">Additional Features</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={showAdditionalFeatures}
                      onCheckedChange={setShowAdditionalFeatures}
                    />
                    <span className="text-xs text-muted-foreground">
                      {showAdditionalFeatures ? 'Hide' : 'Show'} Options
                    </span>
                  </div>
                </div>

                {showAdditionalFeatures && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Premium Features with Custom Toggle Pattern */}
                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          specs.features.includes('embossing')
                            ? isDarkMode ? 'bg-accent/10 border-accent/30' : 'bg-accent/5 border-accent/20'
                            : isDarkMode ? 'border-border hover:bg-muted/20' : 'border-border hover:bg-muted/10'
                        }`}
                        onClick={() => {
                          const isAdding = !specs.features.includes('embossing');
                          const newFeatures = isAdding
                            ? [...specs.features, 'embossing']
                            : specs.features.filter(f => f !== 'embossing');
                          
                          // Pre-fill 2x2 default when enabling the feature
                          if (isAdding && (!specs.featureAreas.embossing || (specs.featureAreas.embossing.length === 0 && specs.featureAreas.embossing.width === 0))) {
                            onFeatureAreaChange('embossing', 2, 2);
                          }
                          onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        {specs.features.includes('embossing') ? (
                          <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        ) : (
                          <div className={`w-4 h-4 rounded border flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-slate-300'}`}></div>
                        )}
                        <span className="text-card-foreground">Embossing</span>
                      </div>

                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          specs.features.includes('debossing')
                            ? isDarkMode ? 'bg-accent/10 border-accent/30' : 'bg-accent/5 border-accent/20'
                            : isDarkMode ? 'border-border hover:bg-muted/20' : 'border-border hover:bg-muted/10'
                        }`}
                        onClick={() => {
                          const isAdding = !specs.features.includes('debossing');
                          const newFeatures = isAdding
                            ? [...specs.features, 'debossing']
                            : specs.features.filter(f => f !== 'debossing');
                          
                          // Pre-fill 2x2 default when enabling the feature
                          if (isAdding && (!specs.featureAreas.debossing || (specs.featureAreas.debossing.length === 0 && specs.featureAreas.debossing.width === 0))) {
                            onFeatureAreaChange('debossing', 2, 2);
                          }
                          onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        {specs.features.includes('debossing') ? (
                          <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        ) : (
                          <div className={`w-4 h-4 rounded border flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-slate-300'}`}></div>
                        )}
                        <span className="text-card-foreground">Debossing</span>
                      </div>

                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          specs.features.includes('foiling')
                            ? isDarkMode ? 'bg-accent/10 border-accent/30' : 'bg-accent/5 border-accent/20'
                            : isDarkMode ? 'border-border hover:bg-muted/20' : 'border-border hover:bg-muted/10'
                        }`}
                        onClick={() => {
                          const isAdding = !specs.features.includes('foiling');
                          const newFeatures = isAdding
                            ? [...specs.features, 'foiling']
                            : specs.features.filter(f => f !== 'foiling');
                          
                          // Pre-fill 2x2 default when enabling the feature
                          if (isAdding && (!specs.featureAreas.foiling || (specs.featureAreas.foiling.length === 0 && specs.featureAreas.foiling.width === 0))) {
                            onFeatureAreaChange('foiling', 2, 2);
                          }
                          onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        {specs.features.includes('foiling') ? (
                          <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        ) : (
                          <div className={`w-4 h-4 rounded border flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-slate-300'}`}></div>
                        )}
                        <span className="text-card-foreground">Foiling</span>
                      </div>

                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          specs.features.includes('pvc-window')
                            ? isDarkMode ? 'bg-accent/10 border-accent/30' : 'bg-accent/5 border-accent/20'
                            : isDarkMode ? 'border-border hover:bg-muted/20' : 'border-border hover:bg-muted/10'
                        }`}
                        onClick={() => {
                          const isAdding = !specs.features.includes('pvc-window');
                          const newFeatures = isAdding
                            ? [...specs.features, 'pvc-window']
                            : specs.features.filter(f => f !== 'pvc-window');
                          
                          // Pre-fill 2x2 default when enabling the feature
                          if (isAdding && (!specs.featureAreas['pvc-window'] || (specs.featureAreas['pvc-window'].length === 0 && specs.featureAreas['pvc-window'].width === 0))) {
                            onFeatureAreaChange('pvc-window', 2, 2);
                          }
                          onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        {specs.features.includes('pvc-window') ? (
                          <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        ) : (
                          <div className={`w-4 h-4 rounded border flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-slate-300'}`}></div>
                        )}
                        <span className="text-card-foreground">PVC Window</span>
                      </div>

                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          specs.features.includes('handle')
                            ? isDarkMode ? 'bg-accent/10 border-accent/30' : 'bg-accent/5 border-accent/20'
                            : isDarkMode ? 'border-border hover:bg-muted/20' : 'border-border hover:bg-muted/10'
                        }`}
                        onClick={() => {
                          const newFeatures = specs.features.includes('handle')
                            ? specs.features.filter(f => f !== 'handle')
                            : [...specs.features, 'handle'];
                          onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        {specs.features.includes('handle') ? (
                          <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        ) : (
                          <div className={`w-4 h-4 rounded border flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-slate-300'}`}></div>
                        )}
                        <span className="text-card-foreground">Handle</span>
                      </div>

                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          specs.features.includes('spot-uv')
                            ? isDarkMode ? 'bg-accent/10 border-accent/30' : 'bg-accent/5 border-accent/20'
                            : isDarkMode ? 'border-border hover:bg-muted/20' : 'border-border hover:bg-muted/10'
                        }`}
                        onClick={() => {
                          const isAdding = !specs.features.includes('spot-uv');
                          const newFeatures = isAdding
                            ? [...specs.features, 'spot-uv']
                            : specs.features.filter(f => f !== 'spot-uv');
                          
                          // Pre-fill 2x2 default when enabling the feature
                          if (isAdding && (!specs.featureAreas['spot-uv'] || (specs.featureAreas['spot-uv'].length === 0 && specs.featureAreas['spot-uv'].width === 0))) {
                            onFeatureAreaChange('spot-uv', 2, 2);
                          }
                          onSpecsChange({ target: { name: 'features', value: newFeatures } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      >
                        {specs.features.includes('spot-uv') ? (
                          <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        ) : (
                          <div className={`w-4 h-4 rounded border flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-slate-300'}`}></div>
                        )}
                        <span className="text-card-foreground">Spot UV</span>
                      </div>
                    </div>

                    {/* Area inputs for selected features */}
                    {specs.features.some(f => ['embossing', 'debossing', 'foiling', 'pvc-window'].includes(f)) && (
                      <div className="space-y-2">
                        {specs.features.includes('embossing') && renderAreaInputs('embossing', 'Embossing')}
                        {specs.features.includes('debossing') && renderAreaInputs('debossing', 'Debossing')}
                        {specs.features.includes('foiling') && renderAreaInputs('foiling', 'Foiling')}
                        {specs.features.includes('pvc-window') && renderAreaInputs('pvc-window', 'PVC Window')}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Insert Section */}
              <InsertSection
                expanded={showInsertSection}
                onToggle={() => setShowInsertSection(!showInsertSection)}
                insertSpec={currentInsertSpec}
                onInsertChange={handleInsertChange}
                boxQuantity={specs.quantity}
              />
            </TabsContent>

            <TabsContent value="printing" className="space-y-6 mt-0">
              
              {/* Outside Printing */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                  <span className="text-sm font-medium text-card-foreground">Outside Printing</span>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">CMYK Colors</label>
                    <input
                      type="number"
                      name="printing.cmyk"
                      value={specs.printing.cmyk || ''}
                      onChange={(e) => onPrintingChange('cmyk', parseInt(e.target.value), false)}
                      min={0}
                      className="w-full px-2.5 py-2 text-sm border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Pantone Colors</label>
                    <input
                      type="number"
                      name="printing.pantone"
                      value={specs.printing.pantone || ''}
                      onChange={(e) => onPrintingChange('pantone', parseInt(e.target.value), false)}
                      min={0}
                      className="w-full px-2.5 py-2 text-sm border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Metallic Colors</label>
                    <input
                      type="number"
                      name="printing.metallic"
                      value={specs.printing.metallic || ''}
                      onChange={(e) => onPrintingChange('metallic', parseInt(e.target.value), false)}
                      min={0}
                      className="w-full px-2.5 py-2 text-sm border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Inside Printing */}
              <div className={cn(
                "space-y-3",
                insidePrintingDisabled && "opacity-50"
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                    <span className="text-sm font-medium text-card-foreground">Inside Printing</span>
                  </div>
                  {insidePrintingDisabled && (
                    <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                      Not available for single-face corrugated
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">CMYK Colors</label>
                    <input
                      type="number"
                      name="insidePrinting.cmyk"
                      value={specs.insidePrinting.cmyk || ''}
                      onChange={(e) => onPrintingChange('cmyk', parseInt(e.target.value), true)}
                      min={0}
                      disabled={insidePrintingDisabled}
                      className="w-full px-2.5 py-2 text-sm border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground disabled:bg-muted disabled:cursor-not-allowed"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Pantone Colors</label>
                    <input
                      type="number"
                      name="insidePrinting.pantone"
                      value={specs.insidePrinting.pantone || ''}
                      onChange={(e) => onPrintingChange('pantone', parseInt(e.target.value), true)}
                      min={0}
                      disabled={insidePrintingDisabled}
                      className="w-full px-2.5 py-2 text-sm border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground disabled:bg-muted disabled:cursor-not-allowed"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Metallic Colors</label>
                    <input
                      type="number"
                      name="insidePrinting.metallic"
                      value={specs.insidePrinting.metallic || ''}
                      onChange={(e) => onPrintingChange('metallic', parseInt(e.target.value), true)}
                      min={0}
                      disabled={insidePrintingDisabled}
                      className="w-full px-2.5 py-2 text-sm border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-primary bg-input text-foreground disabled:bg-muted disabled:cursor-not-allowed"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* White Underprint (Safaida) - For Kraft Materials Only */}
              {(specs.material === 'kraft' || 
                (specs.material === 'corrugated' && 
                 (specs.corrugatedType === 'single-kraft' || specs.corrugatedType === 'double-kraft'))) && (
                <div className="space-y-3 p-4 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50/50 dark:bg-amber-900/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-amber-600 rounded-full"></div>
                      <span className="text-sm font-medium text-card-foreground">
                        White Underprint (Safaida)
                      </span>
                    </div>
                    <Badge 
                      variant={specs.whiteUnderprint ? "default" : "secondary"} 
                      className={specs.whiteUnderprint ? "bg-amber-600" : ""}
                    >
                      {specs.whiteUnderprint ? 'Included' : 'Not Applied'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-3 pl-2">
                    <span className="text-xs text-muted-foreground">Disabled</span>
                    <Switch
                      checked={specs.whiteUnderprint}
                      onCheckedChange={onWhiteUnderprintChange}
                    />
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Enabled (Recommended)
                    </span>
                  </div>
                  
                  {!specs.whiteUnderprint && (
                    <div className="flex items-start gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                      <span className="text-xs text-yellow-700 dark:text-yellow-400">
                        ⚠️ Without white underprint, colors may appear dull or faded on kraft paper.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="features">
              <div className="space-y-4 mt-0">
                {/* Features content would go here */}
              </div>
            </TabsContent>

            <TabsContent value="shipping" className="space-y-5 mt-0">
              {/* Quantity & SKUs Section */}
              <SkuQuantityManager
                orderMode={orderMode}
                onOrderModeChange={onOrderModeChange || (() => {})}
                quantity={specs.quantity}
                onQuantityChange={(qty) => {
                  const syntheticEvent = {
                    target: { name: 'quantity', value: qty, type: 'number' }
                  } as React.ChangeEvent<HTMLInputElement>;
                  onSpecsChange(syntheticEvent);
                }}
                skus={skus}
                onSkusChange={onSkusChange || (() => {})}
                quantityTiers={quantityTiers}
                onQuantityTiersChange={onQuantityTiersChange || (() => {})}
                selectedTierIndex={selectedTierIndex}
                onSelectedTierChange={onSelectedTierChange || (() => {})}
                isCalculated={hasSuccessfulCalculation}
                tierSavings={tierSavings}
                maxSkus={10}
                maxTiers={5}
                loading={loading}
              />

              {/* Weight & Destination Display - only show after calculation (hide pre-calc weight for multi-qty) */}
              {hasSuccessfulCalculation && (weight > 0 || calculatedWeight > 0) && (
                <div className="flex items-center gap-3 p-3 bg-muted/30 border border-border rounded-lg">
                  <Scale className="h-4 w-4 text-primary" />
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    <span className="text-muted-foreground">
                      Weight: <strong className="text-foreground font-semibold">{(weight || calculatedWeight).toFixed(2)} kg</strong>
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">
                      Destination: <strong className="text-foreground font-semibold">
                        {specs.shippingCountry === 'USA' && '🇺🇸 United States'}
                        {specs.shippingCountry === 'UK' && '🇬🇧 United Kingdom'}
                        {specs.shippingCountry === 'DUBAI' && '🇦🇪 Dubai (UAE)'}
                      </strong>
                    </span>
                  </div>
                </div>
              )}

              {/* Shipping Options - Show after calculation */}
              {allShippingOptions ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-primary" />
                    <h4 className="text-sm font-semibold text-foreground">Shipping Methods</h4>
                  </div>
                  <div className="space-y-2">
                    {shippingOptionsHighestOnly.map((opt) => {
                      const isSelected = selectedShipping?.type === opt.type && selectedShipping?.option === opt.key;
                      return (
                        <button
                          key={`${opt.type}_${opt.key}`}
                          type="button"
                          onClick={() => onShippingSelect?.(opt.type, opt.key)}
                          className={cn(
                            "w-full flex items-center justify-between p-3 border-2 rounded-lg text-left transition-all",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50 bg-background"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                              isSelected ? "border-primary" : "border-border"
                            )}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{opt.displayName}</div>
                              <div className="text-xs text-muted-foreground">{opt.deliveryDays}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-sm">${opt.costUSD.toFixed(0)}</div>
                            <div className="text-xs text-muted-foreground">PKR {opt.cost.toLocaleString()}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Agent Profit Margin */}
                  {onCompanyProfitChange && (
                    <>
                      <div className="border-t border-border pt-4" />
                      <div className="space-y-3 p-4 bg-muted/20 border border-border rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground">Agent Margin</span>
                          <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{companyProfit}%</span>
                        </div>
                        
                        {/* Preset Pills */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {[40, 50, 60, 75, 100].map((percent) => (
                            <button
                              key={percent}
                              type="button"
                              onClick={() => onCompanyProfitChange(percent)}
                              className={cn(
                                "px-3.5 py-2 rounded-md text-xs font-semibold transition-all border",
                                companyProfit === percent
                                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                  : "bg-background border-border hover:border-primary/50 hover:bg-muted/50 text-foreground"
                              )}
                            >
                              {percent}%
                            </button>
                          ))}
                          
                          {/* Custom Input */}
                          <Input
                            type="number"
                            placeholder="Custom"
                            value={![40, 50, 60, 75, 100].includes(companyProfit) ? companyProfit : ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numValue = parseFloat(value);
                              
                              if (!isNaN(numValue) && numValue >= 0 && numValue <= 1000) {
                                onCompanyProfitChange(numValue);
                              } else if (value === "") {
                                onCompanyProfitChange(0);
                              }
                            }}
                            className="w-24 h-9 text-center text-xs font-medium border-border focus:border-primary"
                            min="0"
                            max="1000"
                            step="1"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Before Calculation Message */
                <div className="p-4 bg-muted/30 border border-dashed border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Truck className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Calculate your quote to view available shipping methods
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
        </div>

        <div className="p-4 bg-muted/50 border-t border-border flex justify-between items-center">
            {/* Progress Indicators */}
            <div className="flex items-center gap-1.5">
              {tabs.map((tab, index) => {
                const isActive = activeTab === tab.id;
                const isCompleted = tabs.findIndex(t => t.id === activeTab) > index;
                
                return (
                  <div
                    key={tab.id}
                    className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium cursor-pointer transition-all duration-200",
                      isActive 
                        ? "bg-primary text-primary-foreground shadow-sm" 
                        : isCompleted
                        ? "bg-primary/80 text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-muted border border-border",
                      loading && "opacity-50 cursor-not-allowed"
                    )}
                    onClick={() => !loading && setActiveTab(tab.id)}
                  >
                    {index + 1}
                  </div>
                );
              })}
              
              {/* Progress Bar */}
              <div className="ml-2 w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                  style={{ 
                    width: `${((tabs.findIndex(tab => tab.id === activeTab) + 1) / tabs.length) * 100}%` 
                  }}
                />
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                  if (currentIndex > 0) {
                    setActiveTab(tabs[currentIndex - 1].id);
                  }
                }}
                disabled={activeTab === tabs[0].id || loading}
              >
                Previous
              </Button>
              
              {activeTab === tabs[tabs.length - 1].id ? (
                <Button
                  type="submit"
                  size="sm"
                  disabled={!isFormValid || loading || (hasSuccessfulCalculation && !hasFormChanged)}
                  title={!isFormValid ? 'Please fill in all required dimensions' : undefined}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (hasSuccessfulCalculation && !hasFormChanged) ? (
                    'Calculated'
                  ) : (
                    'Calculate Cost'
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  onClick={(e) => {
                    // Prevent any chance of submit during the same click frame
                    e.preventDefault();
                    e.stopPropagation();
                    handleNextClick();
                  }}
                  disabled={loading}
                >
                  Next
                </Button>
              )}
            </div>
        </div>
      </Tabs>
    </form>
  );
};
